import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { connectInfiniteHits, Highlight } from 'react-instantsearch-dom';
import styled from 'styled-components';
import { ReactComponent as InfoIcon } from '../../../assets/images/svg/info-filled-blue.svg';
import { pxToRem } from '../../../assets/styles/helper';
import themeColors from '../../../assets/styles/themeColors';
import { variables } from '../../../assets/styles/variables';
import config from '../../../config';
import { selectSettings } from '../../../store/selectors/settingsSelectors';

const ListWrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${pxToRem(8)};
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  padding-right: ${pxToRem(4)};
`;

const ProductRow = styled.button`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${pxToRem(12)};
  padding: ${pxToRem(12)};
  border: 1px solid
    ${({ $selected }) =>
      $selected ? themeColors.colorPrimary : themeColors.colorBorderPrimary};
  border-radius: ${pxToRem(6)};
  background: ${themeColors.colorWhite};
  width: 100%;
  cursor: pointer;
  text-align: left;
  flex-shrink: 0;
`;

const ProductLeft = styled.div`
  display: flex;
  align-items: center;
  gap: ${pxToRem(12)};
  flex: 1;
  min-width: 0;
`;

const Radio = styled.span`
  width: ${pxToRem(18)};
  height: ${pxToRem(18)};
  border-radius: 50%;
  border: 2px solid ${themeColors.colorPrimary};
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;

  &::after {
    content: '';
    width: ${pxToRem(8)};
    height: ${pxToRem(8)};
    border-radius: 50%;
    background: ${({ $selected }) =>
      $selected ? themeColors.colorPrimary : 'transparent'};
  }
`;

const Thumb = styled.img`
  width: ${pxToRem(36)};
  height: ${pxToRem(36)};
  object-fit: contain;
  border-radius: ${pxToRem(4)};
`;

const ThumbPlaceholder = styled.div`
  width: ${pxToRem(36)};
  height: ${pxToRem(36)};
  border-radius: ${pxToRem(4)};
  background: ${themeColors.colorBackgroundSecondary};
  flex-shrink: 0;
`;

const ProductName = styled.div`
  font-family: ${variables.family.goldSilver}, sans-serif;
  font-size: ${pxToRem(16)};
  font-weight: 500;
  line-height: ${pxToRem(18)};
  color: ${themeColors.colorTextPrimary};

  .ais-Highlight-highlighted {
    background: ${themeColors.colorWarningnBackground};
    color: ${themeColors.colorTextPrimary};
    font-weight: 700;
  }
`;

const ProductWeight = styled.div`
  font-family: ${variables.family.goldSilver}, sans-serif;
  font-size: ${pxToRem(12)};
  font-weight: 500;
  line-height: 1.4;
  color: ${themeColors.colorLabel};
  flex-shrink: 0;
  white-space: nowrap;
`;

const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: ${pxToRem(12)};
  flex: 1;
  min-height: 0;
  padding: ${pxToRem(16)};
  text-align: center;

  > svg {
    width: ${pxToRem(40)};
    height: ${pxToRem(40)};
  }
`;

const EmptyMessage = styled.div`
  font-family: ${variables.family.goldSilver}, sans-serif;
  font-size: ${pxToRem(18)};
  line-height: 1.4;
  color: ${themeColors.colorTextSecondary};
`;

const SupportLink = styled.a`
  font-family: ${variables.family.goldSilver}, sans-serif;
  font-size: ${pxToRem(14)};
  color: ${themeColors.colorPrimary};
  text-decoration: underline;
`;

const ProductThumb = ({ productCode, rootUrl }) => {
  const [hasError, setHasError] = useState(false);
  if (hasError || !productCode) return <ThumbPlaceholder />;
  const src = config.algoliaSearch.imageCode
    .replace('{product_code}', productCode)
    .replace('{base_url}', rootUrl);
  return (
    <Thumb
      src={encodeURI(src)}
      alt=""
      loading="eager"
      decoding="async"
      onError={() => setHasError(true)}
    />
  );
};

ProductThumb.propTypes = {
  productCode: PropTypes.string,
  rootUrl: PropTypes.string,
};

const SellFromHomeProductList = ({ hits, selectedCode, onSelect }) => {
  const { t } = useTranslation();
  const { RootUrl } = useSelector(selectSettings);
  const phone = t('sellWizard.sfhWizard.phone');

  const validHits = hits.filter((h) => h.Caption && h.Caption.trim());

  if (validHits.length === 0) {
    return (
      <EmptyState>
        <InfoIcon />
        <EmptyMessage>{t('sellWizard.sfhWizard.noResultsTitle')}</EmptyMessage>
        <SupportLink href={`tel:${phone.replace(/[^\d+]/g, '')}`}>
          {t('sellWizard.sfhWizard.needHelp')} {phone}
        </SupportLink>
      </EmptyState>
    );
  }

  return (
    <ListWrap>
      {validHits.map((hit) => {
        const isSelected = selectedCode === hit.Code;
        return (
          <ProductRow
            type="button"
            key={hit.objectID}
            $selected={isSelected}
            onClick={() => onSelect(hit)}
          >
            <ProductLeft>
              <Radio $selected={isSelected} />
              <ProductThumb productCode={hit.Code} rootUrl={RootUrl} />
              <ProductName>
                <Highlight attribute="Caption" hit={hit} />
              </ProductName>
            </ProductLeft>
            <ProductWeight>{hit.WeightCaption}</ProductWeight>
          </ProductRow>
        );
      })}
    </ListWrap>
  );
};

SellFromHomeProductList.propTypes = {
  hits: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  selectedCode: PropTypes.string,
  onSelect: PropTypes.func.isRequired,
};

export default connectInfiniteHits(SellFromHomeProductList);
